import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Receipt } from '@/pages/Sales/Receipt';
import { TableDataProps } from '@/pages/SalesList';

interface ReceiptDialogProps {
  sale: TableDataProps | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReceiptDialog: React.FC<ReceiptDialogProps> = ({
  sale,
  open,
  onOpenChange,
}) => {
  if (!sale) return null;

  const mainAddress = sale?.client?.addresses?.find(addr => addr?.pivot?.is_main) || sale?.client?.addresses?.[0];

  // Busca o endereço do step de entrega (Em transporte = 5, Entregue = 6)
  const deliveryStepAddress = sale?.process_step_sales?.find(
    step => [5, 6].includes(step?.sale_step_id) && step?.address
  )?.address;

  // Formata objeto de endereço para string legível (formato similar ao Google Maps)
  // Exemplo: "Rua Frei Odilon, 68 - Álvaro Weyne, Fortaleza - CE, 60336-190"
  // Complemento fica em linha separada
  const formatAddressObject = (addr: {
    street?: string | null;
    number?: string | null;
    complement?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    addressName?: string | null;
  }) => {
    const parts: string[] = [];

    // Parte 1: Rua e número (ex: "Rua Frei Odilon, 68")
    if (addr.street) {
      let streetPart = addr.street;
      if (addr.number) streetPart += `, ${addr.number}`;
      parts.push(streetPart);
    }

    // Parte 2: Bairro (ex: "- Álvaro Weyne")
    if (addr.neighborhood) {
      parts.push(`- ${addr.neighborhood}`);
    }

    // Parte 3: Cidade e Estado (ex: "Fortaleza - CE")
    if (addr.city) {
      let locationPart = addr.city;
      if (addr.state) locationPart += ` - ${addr.state}`;
      // Adiciona CEP junto se existir (ex: "Fortaleza - CE, 60336-190")
      if (addr.zip_code) locationPart += `, ${addr.zip_code}`;
      parts.push(locationPart);
    } else if (addr.zip_code) {
      parts.push(addr.zip_code);
    }

    // Monta o endereço base
    let formattedAddress = parts.length > 0 ? parts.join(' ') : "";

    // Se não conseguiu montar o endereço pelos campos, usa o addressName completo
    if (!formattedAddress && addr.addressName) {
      // Remove "Brazil" do final se existir
      formattedAddress = addr.addressName.replace(/, Brazil$/, '').trim();
    }

    // Adiciona complemento em linha separada no final
    if (addr.complement && formattedAddress) {
      formattedAddress = `${formattedAddress}\n${addr.complement}`;
    } else if (addr.complement) {
      formattedAddress = addr.complement;
    }

    return formattedAddress || "Não informado";
  };

  // Construir endereço de entrega: prioridade: delivery_address > step de entrega > endereço do cliente
  const buildDeliveryAddress = () => {
    // Se tem delivery_address salvo, usa ele
    if (sale?.delivery_address) {
      // Se for um objeto (SEDEX), formata os campos
      if (typeof sale.delivery_address === 'object') {
        return formatAddressObject(sale.delivery_address);
      }
      // Se for string JSON (começa com {), faz parse e formata
      if (typeof sale.delivery_address === 'string' && sale.delivery_address.trim().startsWith('{')) {
        try {
          const addr = JSON.parse(sale.delivery_address);
          return formatAddressObject(addr);
        } catch {
          // Se falhar o parse, retorna a string original
          return sale.delivery_address;
        }
      }
      // Se for string normal, retorna diretamente
      return sale.delivery_address;
    }

    // Se tem endereço no step de entrega, usa ele
    if (deliveryStepAddress) {
      return formatAddressObject(deliveryStepAddress);
    }

    // Senão, monta a partir do endereço principal do cliente
    if (mainAddress) {
      return formatAddressObject(mainAddress);
    }
    return "Não informado";
  };

  const receiptData = {
    id: sale?.id,
    clientName: sale?.client?.name,
    phone: sale?.client?.phone || "",
    seller: sale?.seller?.user?.name,
    clientOrigin: sale?.client?.client_origin?.name || null,
    isNewClient: sale?.is_new_client,
    delivery_address: buildDeliveryAddress(),
    address: {
      street: mainAddress?.street || "",
      number: mainAddress?.number || "",
      neighborhood: mainAddress?.neighborhood || "",
      city: mainAddress?.city || "",
      state: mainAddress?.state || "",
      zipCode: mainAddress?.zip_code || "",
    },
    circuit_route: {
      name: sale?.circuit_route?.title || ""
    },
    products: sale?.products?.map(product => ({
      name: product?.product_name + " - " + product?.product_classification?.name,
      quantity: product?.pivot?.quantity,
      price: parseFloat(product?.pivot?.price_unit),
      contest: {
        name: product?.contest?.name || "",
        lottery: {
          name: product?.contest?.lottery?.name || ""
        }
      },
      product_classification: {
        quota_quantity: product?.product_classification?.quota_quantity || "",
        name: product?.product_classification?.name || "",
        game_quantity: product?.product_classification?.game_quantity || "",
        dez_quantity: product?.product_classification?.dez_quantity || ""
      }
    })),
    paymentMethod: sale?.payment_forms?.map(form => form?.payment_form.name).join(", "),
    payment_forms: sale?.payment_forms?.map(form => ({
      payment_form: {
        name: form?.payment_form?.name
      },
      value: form?.value
    })),
    observations: ""
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto print:max-h-none print:overflow-visible print:h-auto">
        <DialogHeader>
          <DialogTitle>Comprovante de Venda</DialogTitle>
        </DialogHeader>
        <Receipt data={receiptData} />
      </DialogContent>
    </Dialog>
  );
}; 